import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Sparkles, Heart, Instagram, ChevronRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import api from '../utils/api';
import useEmblaCarousel from 'embla-carousel-react';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [emblaRef] = useEmblaCarousel({ loop: true, align: 'start' });

  useEffect(() => {
    fetchFeaturedProducts();
    fetchBanners();
    api.get('/instagram-posts').then(r => setInstagramPosts(r.data || [])).catch(() => {});
    api.get('/site-settings').then(r => setSiteSettings(r.data || {})).catch(() => {});
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await api.get('/products?featured=true');
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchBanners = async () => {
    try {
      const response = await api.get('/banners?page=home');
      setBanners(response.data || []);
    } catch {}
  };

  // Scroll to #vastram-mirror if hash present
  useEffect(() => {
    if (window.location.hash === '#vastram-mirror') {
      setTimeout(() => {
        const el = document.getElementById('vastram-mirror');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative h-[90vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${siteSettings.hero_image || 'https://static.prod-images.emergentagent.com/jobs/57c23f7f-ef4e-4b2a-addb-30eed1f0df0f/images/840f0a215036c5e3d383c4c3f712c92e7b9ba2176a29f26edc18e74909a3777d.png'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
        
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl text-ivory mb-6 tracking-tight"
            data-testid="hero-headline"
          >
            Every Saree Tells a <em className="italic">Story</em>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg sm:text-xl text-ivory mb-10 max-w-2xl mx-auto leading-relaxed font-light"
          >
            Discover timeless elegance in every drape. Handcrafted sarees and suits that celebrate tradition with modern grace.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/products?category=saree"
              className="bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em]"
              data-testid="explore-sarees-button"
            >
              Explore Sarees
            </Link>
            <Link
              to="/products?category=suit"
              className="border border-ivory text-ivory bg-transparent px-8 py-4 rounded-none hover:bg-ivory hover:text-forest transition-colors text-sm uppercase tracking-[0.1em]"
              data-testid="discover-suits-button"
            >
              Discover Suits
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Sale/Discount Banners */}
      {banners.length > 0 && (
        <section data-testid="sale-banners">
          {banners.map((banner) => (
            <Link
              key={banner.banner_id}
              to={banner.link_url || '/products'}
              className="block"
              data-testid={`banner-${banner.banner_id}`}
            >
              <div
                className="py-3 px-4 text-center transition-opacity hover:opacity-90"
                style={{ backgroundColor: banner.bg_color, color: banner.text_color }}
              >
                <div className="max-w-7xl mx-auto flex items-center justify-center space-x-3">
                  <span className="text-sm sm:text-base font-medium tracking-wide">{banner.title}</span>
                  {banner.subtitle && (
                    <>
                      <span className="hidden sm:inline opacity-60">|</span>
                      <span className="hidden sm:inline text-sm opacity-90">{banner.subtitle}</span>
                    </>
                  )}
                  <ChevronRight className="w-4 h-4 opacity-70" />
                </div>
              </div>
            </Link>
          ))}
        </section>
      )}

      {/* Category Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="category-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Sarees */}
            <Link
              to="/products?category=saree"
              className="relative group overflow-hidden rounded-sm h-[500px]"
              data-testid="category-sarees"
            >
              <img
                src={siteSettings.saree_section_image || "https://images.unsplash.com/photo-1739429945557-22682b674508?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB3b21hbiUyMGVsZWdhbnQlMjBzYXJlZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTE0OTk5MHww&ixlib=rb-4.1.0&q=85"}
                alt="Sarees"
                className="w-full h-full object-cover product-image-hover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
                <div>
                  <h2 className="font-heading text-4xl text-ivory mb-2">Sarees</h2>
                  <p className="text-gold text-sm uppercase tracking-[0.2em] font-medium">Timeless Elegance</p>
                </div>
              </div>
            </Link>

            {/* Suits */}
            <Link
              to="/products?category=suit"
              className="relative group overflow-hidden rounded-sm h-[500px]"
              data-testid="category-suits"
            >
              <img
                src={siteSettings.suit_section_image || "https://images.unsplash.com/photo-1765529374855-7353c3c47fc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBldGhuaWMlMjBzdWl0JTIwdHJhZGl0aW9uYWwlMjB3ZWFyfGVufDB8fHx8MTc3NTE0OTk5MHww&ixlib=rb-4.1.0&q=85"}
                alt="Suits"
                className="w-full h-full object-cover product-image-hover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
                <div>
                  <h2 className="font-heading text-4xl text-ivory mb-2">Suits</h2>
                  <p className="text-gold text-sm uppercase tracking-[0.2em] font-medium">Modern Grace</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Vastram Mirror Description */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" id="vastram-mirror" data-testid="vastram-mirror-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-sm border border-gold/30"
          >
            <div className="flex flex-col md:flex-row items-center">
              <div className="flex-1 p-10 md:p-14">
                <div className="flex items-center mb-4">
                  <Sparkles className="w-6 h-6 text-gold mr-2" />
                  <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">AI-Powered Virtual Try-On</span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl text-forest mb-4 tracking-tight">
                  See It On <em className="italic">You</em> Before You Buy
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4 max-w-lg">
                  Vastram Mirror uses AI to generate a virtual try-on image — see how any saree or suit looks on you instantly, right from the product page.
                </p>
                <p className="text-sm text-forest/70 leading-relaxed mb-8 max-w-lg">
                  Browse our collection, open any product you love, and tap the <strong>Mirror</strong> icon. Upload or capture your photo once — it's saved for future use. Get up to 3 virtual try-ons per day, completely free.
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em]"
                  data-testid="vastram-mirror-cta-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Try on a Product
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
              <div className="hidden md:block w-px self-stretch bg-gold/20"></div>
              <div className="flex-1 p-10 md:p-14 flex flex-col items-center justify-center text-center">
                <div className="space-y-6 w-full max-w-xs">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-forest font-heading text-lg">1</span>
                    </div>
                    <p className="text-forest text-sm text-left">Browse products &amp; tap the Mirror icon</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-forest font-heading text-lg">2</span>
                    </div>
                    <p className="text-forest text-sm text-left">Upload or capture your photo (saved for next time)</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-forest font-heading text-lg">3</span>
                    </div>
                    <p className="text-forest text-sm text-left">AI generates a try-on — see yourself wearing it!</p>
                  </div>
                  <div className="pt-4 border-t border-gold/20">
                    <p className="text-xs text-muted-foreground italic">3 free tries per day · Photo stays private &amp; secure</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Vastram Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-neutral-warm" data-testid="why-vastram-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl text-center text-forest mb-16 tracking-tight">
            Why <em className="italic">Vastram</em>?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center space-y-4" data-testid="trust-quality">
              <div className="flex justify-center">
                <ShieldCheck className="w-12 h-12 text-gold" />
              </div>
              <h3 className="font-heading text-2xl text-forest">Premium Quality</h3>
              <p className="text-muted-foreground leading-relaxed">
                Every piece is carefully curated and quality-checked to ensure you receive only the finest fabrics and craftsmanship.
              </p>
            </div>

            <div className="text-center space-y-4" data-testid="trust-comfort">
              <div className="flex justify-center">
                <Sparkles className="w-12 h-12 text-gold" />
              </div>
              <h3 className="font-heading text-2xl text-forest">Comfortable Elegance</h3>
              <p className="text-muted-foreground leading-relaxed">
                We believe beauty shouldn't compromise comfort. Our garments are designed to make you feel as good as you look.
              </p>
            </div>

            <div className="text-center space-y-4" data-testid="trust-authenticity">
              <div className="flex justify-center">
                <Heart className="w-12 h-12 text-gold" />
              </div>
              <h3 className="font-heading text-2xl text-forest">Authentic Heritage</h3>
              <p className="text-muted-foreground leading-relaxed">
                Supporting artisans and celebrating Indian traditions with every thread, weave, and embroidery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl text-forest tracking-tight">
              Featured <em className="italic">Collection</em>
            </h2>
            <Link
              to="/products"
              className="text-sm uppercase tracking-[0.2em] text-forest underline underline-offset-4 decoration-gold/50 hover:decoration-gold"
              data-testid="view-all-link"
            >
              View All
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.slice(0, 4).map((product) => (
              <ProductCard key={product.product_id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-neutral-warm" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl text-center text-forest mb-16 tracking-tight">
            What Our <em className="italic">Customers</em> Say
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#E8E2D9]/50 p-8 rounded-sm border border-[#C2A46D]/20" data-testid="testimonial-1">
              <p className="text-muted-foreground mb-4 leading-relaxed italic">
                "The quality of the saree exceeded my expectations. The fabric is luxurious and the craftsmanship is impeccable. I felt truly special wearing it."
              </p>
              <p className="text-sm uppercase tracking-[0.2em] text-forest">Priya Sharma</p>
            </div>

            <div className="bg-[#E8E2D9]/50 p-8 rounded-sm border border-[#C2A46D]/20" data-testid="testimonial-2">
              <p className="text-muted-foreground mb-4 leading-relaxed italic">
                "Vastram understands what modern Indian women want - traditional elegance with contemporary comfort. My suit fits perfectly!"
              </p>
              <p className="text-sm uppercase tracking-[0.2em] text-forest">Anjali Patel</p>
            </div>

            <div className="bg-[#E8E2D9]/50 p-8 rounded-sm border border-[#C2A46D]/20" data-testid="testimonial-3">
              <p className="text-muted-foreground mb-4 leading-relaxed italic">
                "Every purchase feels personal. The attention to detail and customer care is outstanding. Vastram has become my go-to for special occasions."
              </p>
              <p className="text-sm uppercase tracking-[0.2em] text-forest">Meera Reddy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram & Video Gallery */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl text-forest mb-4 tracking-tight">
              Real <em className="italic">Women</em>, Real Stories
            </h2>
            <a
              href="https://www.instagram.com/thevastramstore.shop/"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-forest hover:text-gold transition-colors"
              data-testid="instagram-link"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-sm uppercase tracking-[0.2em]">@thevastramstore.shop</span>
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(instagramPosts.length > 0 ? instagramPosts.slice(0, 8) : [
              { image_url: 'https://images.unsplash.com/photo-1739429945557-22682b674508?w=400', caption: '', instagram_url: '' },
              { image_url: 'https://images.unsplash.com/photo-1739429945851-cf348b087e3d?w=400', caption: '', instagram_url: '' },
              { image_url: 'https://images.unsplash.com/photo-1739429944341-db4eefcd230a?w=400', caption: '', instagram_url: '' },
              { image_url: 'https://images.unsplash.com/photo-1765529374855-7353c3c47fc6?w=400', caption: '', instagram_url: '' },
            ]).map((post, idx) => (
              post.media_type === 'youtube' && post.youtube_id ? (
                <div key={post.post_id || idx} className="aspect-square overflow-hidden rounded-sm relative group" data-testid="media-post">
                  <iframe
                    src={`https://www.youtube.com/embed/${post.youtube_id}?rel=0`}
                    title={post.caption || `Video ${idx + 1}`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <a key={post.post_id || idx} href={post.instagram_url || 'https://www.instagram.com/thevastramstore.shop/'} target="_blank" rel="noopener noreferrer" className="aspect-square overflow-hidden rounded-sm group" data-testid="instagram-post">
                  <img src={post.image_url} alt={post.caption || `Post ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </a>
              )
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-forest text-ivory" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl mb-6 tracking-tight">
            Find Your <em className="italic">Perfect</em> Saree
          </h2>
          <p className="text-lg mb-10 text-ivory/90 leading-relaxed">
            Not sure which saree suits you best? Take our quick quiz and discover your perfect match.
          </p>
          <Link
            to="/find-your-saree"
            className="inline-block bg-gold text-forest px-8 py-4 rounded-none hover:bg-ivory transition-colors text-sm uppercase tracking-[0.1em]"
            data-testid="quiz-cta-button"
          >
            Start the Quiz
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
